// src/pages/portal/PortalSettingsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout, { NAV_FEATURE_MAP, NAV_PERMISSION_MAP } from './PortalLayout';
import { tenantService, diningOptionService, paymentMethodService, translationService, payrollConfigService, printingService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import useIsMobile from '../../hooks/useIsMobile';
import MobileTabSelect from '../../components/common/MobileTabSelect';
import EodSettingsSection from '../../components/portal/EodSettingsSection';
import CataloguesSection from './CataloguesSection';
import LimitReached from '../../components/portal/LimitReached';
import LogoUploader from '../../components/portal/LogoUploader';
import LockOverlay from '../../components/portal/LockOverlay';
import { atLimit } from '../../utils/featureGating';
import { probePrinter } from '../../utils/eposClient';
import host from '../../host';
import { C } from '../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// Grid snap as fraction of canvas dimensions
const SNAP_X = 20 / 800;
const SNAP_Y = 20 / 500;

// Default values if not yet in tenant config
const DEFAULT_STATIONS    = ['Kitchen', 'Bar', 'Cashier', 'FOH'];
const DEFAULT_DEPARTMENTS = ['Management', 'Kitchen', 'Service', 'Bar'];
const DEFAULT_DAILY_CATS  = ['Inventory', 'Equipment', 'Maintenance', 'Other'];
const DEFAULT_OH_CATS     = ['Rent', 'Utilities', 'Bills', 'Other'];

// Migrate absolute pixel coords (legacy) → 0-1 percentages
const toPercent = (t) => ({
    ...t,
    x: t.x > 1 ? +(t.x / 800).toFixed(4) : t.x,
    y: t.y > 1 ? +(t.y / 500).toFixed(4) : t.y,
});

// ─── Toast ────────────────────────────────────────────────────────────────────
const ToastContainer = ({ toasts }) => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
            <div key={t.id} style={{
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: t.type === 'success' ? '#eff5ff' : C.redLight,
                border: `1.5px solid ${t.type === 'success' ? '#86efac' : C.redBorder}`,
                color: t.type === 'success' ? '#16a34a' : C.red,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontFamily: FONT, pointerEvents: 'auto', minWidth: 220,
            }}>
                {t.message}
            </div>
        ))}
    </div>
);

// ─── Shared input style ────────────────────────────────────────────────────────
const inp = (extra = {}) => ({
    width: '100%', boxSizing: 'border-box',
    fontFamily: FONT, fontSize: 13, color: C.text,
    padding: '8px 12px', borderRadius: 8,
    border: `1.5px solid ${C.border}`, background: C.white,
    outline: 'none',
    ...extra,
});

// ─── Shared label style ───────────────────────────────────────────────────────
const labelStyle = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    letterSpacing: 0.8, textTransform: 'uppercase',
    color: C.muted, display: 'block', marginBottom: 6,
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, description }) => (
    <div style={{ marginBottom: 28, paddingBottom: 16, borderBottom: `1.5px solid ${C.border}` }}>
        <h2 style={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color: C.text, margin: 0 }}>{title}</h2>
        {description && (
            <p style={{ fontFamily: FONT, fontSize: 13, color: C.muted, margin: '6px 0 0', lineHeight: 1.55 }}>
                {description}
            </p>
        )}
    </div>
);

// ─── Section divider label ────────────────────────────────────────────────────
const SubLabel = ({ children, mt = 24 }) => (
    <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: C.muted, marginBottom: 8, marginTop: mt }}>
        {children}
    </div>
);

// ─── Grouped settings card (icon header + body) ────────────────────────────────
const GroupCard = ({ icon, title, description, accent = C.green, accentBg = C.greenLight, children }) => (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: 24, marginBottom: 20, maxWidth: 860 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{
                width: 40, height: 40, borderRadius: 10, background: accentBg, color: accent,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                {icon}
            </div>
            <div>
                <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>
                {description && (
                    <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
                        {description}
                    </div>
                )}
            </div>
        </div>
        {children}
    </div>
);

const IconTeamGroup = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);
const IconExpensesGroup = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/>
        <line x1="9" y1="8" x2="15" y2="8"/><line x1="9" y1="12" x2="15" y2="12"/>
    </svg>
);

// ─── Save button ──────────────────────────────────────────────────────────────
const SaveBtn = ({ onClick, saving, label, t }) => (
    <button onClick={onClick} disabled={saving} style={{
        padding: '9px 22px', borderRadius: 9, border: 'none',
        background: C.green, color: C.white, cursor: saving ? 'not-allowed' : 'pointer',
        fontFamily: FONT, fontSize: 13, fontWeight: 700,
        opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
    }}>
        {saving ? ((t && t('saving')) || 'Saving...') : (label || (t && t('save_changes')) || 'Save Changes')}
    </button>
);

// ─── Delete button ────────────────────────────────────────────────────────────
const DelBtn = ({ onClick }) => (
    <button onClick={onClick} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: C.mutedLight, padding: '2px 4px',
        fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center',
        transition: 'color 0.15s',
    }}
        onMouseEnter={e => e.currentTarget.style.color = C.red}
        onMouseLeave={e => e.currentTarget.style.color = C.mutedLight}
    >
        ×
    </button>
);

// ─── ListPanel (reusable: stations, departments, expense categories) ──────────
const ListPanel = ({ title, description, items, onAdd, onRemove, placeholder, locked = [] }) => {
    const { t } = useLanguage();
    const [input, setInput] = useState('');

    const handleAdd = () => {
        const v = input.trim();
        if (!v || items.includes(v)) return;
        onAdd(v);
        setInput('');
    };

    return (
        <div style={{
            background: C.white, borderRadius: 12, border: `1.5px solid ${C.border}`,
            padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
        }}>
            <div>
                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
                {description && <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.4 }}>{description}</div>}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 28 }}>
                {items.length === 0 && (
                    <span style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight, fontStyle: 'italic' }}>{t('no_items_yet') || 'No items yet'}</span>
                )}
                {items.map(item => {
                    const isLocked = locked.includes(item);
                    return (
                        <span key={item} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: C.greenLight, color: C.green,
                            fontSize: 12, fontWeight: 600, fontFamily: FONT,
                            padding: '3px 8px 3px 10px', borderRadius: 6,
                            border: `1px solid ${C.greenBorder}`,
                        }}>
                            {item}
                            {!isLocked && <DelBtn onClick={() => onRemove(item)} />}
                        </span>
                    );
                })}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
                <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    placeholder={placeholder || 'Type and press Enter...'}
                    style={{ ...inp(), flex: 1, height: 36 }}
                />
                <button onClick={handleAdd} style={{
                    padding: '0 14px', height: 36, borderRadius: 8,
                    background: C.green, color: C.white, border: 'none',
                    cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 700,
                    flexShrink: 0,
                }}>{t('add') || 'Add'}</button>
            </div>
        </div>
    );
};

// ─── TableLayoutModal ─────────────────────────────────────────────────────────
const TableLayoutModal = ({ diningOptionName, tables: rawTables, onSave, onClose }) => {
    const { t } = useLanguage();
    const [tables, setTables] = useState(() =>
        (rawTables || [])
            .filter(t => t && typeof t === 'object' && 'number' in t)
            .map(toPercent)
    );
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const isMobile = useIsMobile();
    const canvasRef = useRef(null);
    const dragRef = useRef(null);
    const getPoint = (e) => (e.touches && e.touches[0]) ? e.touches[0] : e;

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const justClickedTable = useRef(false);

    const handleMouseDown = (e, num) => {
        if (e.cancelable) e.preventDefault();
        const p = getPoint(e);
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const table = tables.find(t => t.number === num);
        if (!table) return;
        const centerX = rect.left + table.x * rect.width;
        const centerY = rect.top + table.y * rect.height;
        dragRef.current = { num, offsetX: p.clientX - centerX, offsetY: p.clientY - centerY };
        justClickedTable.current = true;
        setSelected(num);
    };

    const handleCanvasClick = () => {
        if (justClickedTable.current) {
            justClickedTable.current = false;
            return;
        }
        setSelected(null);
    };

    const handleMouseMove = useCallback((e) => {
        if (!dragRef.current) return;
        if (e.touches && e.cancelable) e.preventDefault(); // stop page scroll while dragging on touch
        const p = (e.touches && e.touches[0]) ? e.touches[0] : e;
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        const { num, offsetX, offsetY } = dragRef.current;
        const rawX = (p.clientX - offsetX - rect.left) / rect.width;
        const rawY = (p.clientY - offsetY - rect.top) / rect.height;
        const x = Math.max(0.03, Math.min(0.97, Math.round(rawX / SNAP_X) * SNAP_X));
        const y = Math.max(0.03, Math.min(0.97, Math.round(rawY / SNAP_Y) * SNAP_Y));
        setTables(prev => prev.map(t => t.number === num ? { ...t, x, y } : t));
    }, []);

    const handleMouseUp = useCallback(() => { dragRef.current = null; }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('touchmove', handleMouseMove, { passive: false });
        window.addEventListener('touchend', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(tables); }
        finally { setSaving(false); }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(15,23,42,0.75)',
            display: 'flex', flexDirection: 'column',
        }}>
            {/* Header */}
            <div style={{
                background: C.white, borderBottom: `1.5px solid ${C.border}`,
                padding: isMobile ? '12px 16px' : '14px 24px', display: 'flex', alignItems: 'center',
                gap: isMobile ? 8 : 16, flexShrink: 0, flexWrap: 'wrap',
            }}>
                <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: C.text }}>
                        {t('table_layout') || 'Table Layout'} &mdash; {diningOptionName}
                    </div>
                    <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 2 }}>
                        {t('drag_tables_to_reposition') || 'Drag tables to reposition. Grid snaps to 20px.'}
                    </div>
                </div>
                <SaveBtn onClick={handleSave} saving={saving} label={t('save_layout') || 'Save Layout'} t={t} />
                <button onClick={onClose} style={{
                    padding: '8px 16px', borderRadius: 8,
                    background: C.bg, border: `1.5px solid ${C.border}`,
                    cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                }}>
                    {t('close') || 'Close'}
                </button>
            </div>

            {/* Canvas + Properties Panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'auto' : 'hidden', WebkitOverflowScrolling: 'touch' }}>
                {/* Canvas */}
                <div style={{ flex: isMobile ? 'none' : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 24, overflow: 'hidden' }}>
                    <div
                        ref={canvasRef}
                        onClick={handleCanvasClick}
                        style={{
                            position: 'relative',
                            width: '100%', maxWidth: 920, height: isMobile ? '56vh' : 520, touchAction: 'none',
                            background: C.white,
                            border: `2px solid ${C.border}`,
                            borderRadius: 14,
                            overflow: 'hidden',
                            backgroundImage: `linear-gradient(to right, ${C.border} 1px, transparent 1px), linear-gradient(to bottom, ${C.border} 1px, transparent 1px)`,
                            backgroundSize: '40px 40px',
                            userSelect: 'none',
                            cursor: 'default',
                        }}
                    >
                        {tables.length === 0 && (
                            <div style={{
                                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontFamily: FONT, fontSize: 14, color: C.mutedLight,
                            }}>
                                {t('no_tables_configured') || 'No tables configured for this dining option'}
                            </div>
                        )}
                        {tables.map(table => {
                            const isSel = selected === table.number;
                            const sz = table.size || 52;
                            return (
                                <div
                                    key={table.number}
                                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, table.number); }}
                                    onTouchStart={(e) => { e.stopPropagation(); handleMouseDown(e, table.number); }}
                                    style={{
                                        position: 'absolute',
                                        left: `${table.x * 100}%`,
                                        top: `${table.y * 100}%`,
                                        transform: 'translate(-50%, -50%)',
                                        width: sz, height: sz,
                                        borderRadius: table.shape === 'square' ? 10 : '50%',
                                        background: table.color || '#3B82F6',
                                        border: `3px solid ${isSel ? C.text : 'rgba(255,255,255,0.4)'}`,
                                        boxShadow: isSel ? `0 0 0 3px ${C.green}, 0 4px 12px rgba(0,0,0,0.2)` : '0 2px 8px rgba(0,0,0,0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'grab',
                                        color: '#fff', fontFamily: FONT, fontSize: 13, fontWeight: 800,
                                        zIndex: isSel ? 10 : 1,
                                        transition: 'box-shadow 0.15s',
                                    }}
                                >
                                    {table.number}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Properties Panel */}
                <div style={{
                    width: isMobile ? '100%' : 220, background: C.white,
                    borderLeft: isMobile ? 'none' : `1.5px solid ${C.border}`,
                    borderTop: isMobile ? `1.5px solid ${C.border}` : 'none',
                    padding: 18, flexShrink: 0, overflowY: 'auto',
                }}>
                    <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 14 }}>
                        {t('table_properties') || 'Properties'}
                    </div>
                    {(() => {
                        const sel = tables.find(t => t.number === selected);
                        if (!sel) return (
                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.mutedLight }}>
                                {t('click_table_to_edit') || 'Click a table to edit'}
                            </div>
                        );
                        const update = (key, val) => setTables(prev => prev.map(t => t.number === selected ? { ...t, [key]: val } : t));
                        return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 4 }}>
                                        {t('shape') || 'Shape'}
                                    </label>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        {['circle', 'square'].map(s => (
                                            <button key={s} onClick={() => update('shape', s)} style={{
                                                flex: 1, padding: '6px 0', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                border: `1.5px solid ${sel.shape === s ? C.green : C.border}`,
                                                background: sel.shape === s ? C.greenLight : C.white,
                                                color: sel.shape === s ? C.green : C.muted,
                                                cursor: 'pointer', fontFamily: FONT, textTransform: 'capitalize',
                                            }}>{s}</button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 4 }}>
                                        {t('color') || 'Color'}
                                    </label>
                                    <input type="color" value={sel.color || '#3B82F6'} onChange={e => update('color', e.target.value)}
                                        style={{ width: '100%', height: 32, borderRadius: 6, border: `1.5px solid ${C.border}`, cursor: 'pointer' }} />
                                </div>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 4 }}>
                                        {t('size') || 'Size'} ({sel.size || 52}px)
                                    </label>
                                    <input type="range" min="36" max="100" value={sel.size || 52} onChange={e => update('size', parseInt(e.target.value))}
                                        style={{ width: '100%', accentColor: C.green }} />
                                </div>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 4 }}>
                                        {t('capacity') || 'Capacity'}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={sel.capacity || 4}
                                        onChange={e => update('capacity', parseInt(e.target.value) || 4)}
                                        style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, fontFamily: FONT }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: C.muted, display: 'block', marginBottom: 4 }}>
                                        {t('section') || 'Section'}
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={t('section_placeholder') || 'e.g. Terrace'}
                                        value={sel.section || ''}
                                        onChange={e => update('section', e.target.value || null)}
                                        style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, fontFamily: FONT }}
                                    />
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        </div>
    );
};

// ─── DiningOptionModal (add / edit) ──────────────────────────────────────────
const DiningOptionModal = ({ editing, initial, onSave, onClose }) => {
    const { t } = useLanguage();
    const [name, setName] = useState(initial?.name || '');
    const [tableType, setTableType] = useState(initial?.tableType || 'tables');
    const [tableCount, setTableCount] = useState(initial?.tableCount || 5);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleSave = async () => {
        if (!name.trim()) return;
        setSaving(true);
        await onSave({ name: name.trim(), tableType, tableCount: parseInt(tableCount) || 1 });
        setSaving(false);
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1500,
            background: 'rgba(15,23,42,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div className="portal-modal-card" style={{
                background: C.white, borderRadius: 16, padding: 28,
                width: 420, maxWidth: '90vw',
                boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
            }}>
                <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 22 }}>
                    {editing ? (t('edit_dining_option') || 'Edit Dining Option') : (t('add_dining_option') || 'Add Dining Option')}
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>{t('name') || 'Name'}</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder={t('placeholder_dining_option')} style={inp({ height: 38 })} />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>{t('table_configuration') || 'Table Configuration'}</label>
                    <select value={tableType} onChange={e => setTableType(e.target.value)} style={inp({ height: 38 })}>
                        <option value="tables">{t('fixed_tables') || 'Fixed tables (with layout designer)'}</option>
                        <option value="random_number">{t('any_table_number') || 'Any table number (random)'}</option>
                        <option value="no_tables">{t('no_tables') || 'No tables'}</option>
                    </select>
                </div>

                {tableType === 'tables' && (
                    <div style={{ marginBottom: 16 }}>
                        <label style={labelStyle}>{t('number_of_tables') || 'Number of Tables'}</label>
                        <input
                            type="number" min={1} max={60}
                            value={tableCount}
                            onChange={e => setTableCount(e.target.value)}
                            style={inp({ height: 38 })}
                        />
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                    <button onClick={onClose} style={{
                        flex: 1, padding: '10px 0', borderRadius: 8,
                        border: `1.5px solid ${C.border}`, background: C.white,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.muted,
                    }}>{t('cancel') || 'Cancel'}</button>
                    <button onClick={handleSave} disabled={saving || !name.trim()} style={{
                        flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
                        background: C.green, color: C.white,
                        cursor: (saving || !name.trim()) ? 'not-allowed' : 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        opacity: (saving || !name.trim()) ? 0.7 : 1,
                    }}>
                        {saving ? (t('saving') || 'Saving...') : (editing ? (t('update') || 'Update') : (t('add_option') || 'Add Option'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Business
// ═════════════════════════════════════════════════════════════════════════════
// ─── Business form primitives ────────────────────────────────────────────────
//
// These MUST stay at module scope. `Field` was previously declared inside
// BusinessSection, so it got a new function identity on every render; React
// compares component types by reference, saw a different type, and unmounted
// the subtree instead of updating it — destroying and recreating the <input>,
// which dropped focus after every keystroke. Covered by
// PortalSettingsPage.focus.test.js.
const G2 = ({ children }) => <div style={{ gridColumn: 'span 2' }}>{children}</div>;

const Field = ({ label, k, form, set, type = 'text', half = false, textarea = false }) => (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
        <label style={labelStyle}>{label}</label>
        {textarea
            ? <textarea value={form[k]} onChange={e => set(k, e.target.value)} rows={3}
                style={{ ...inp(), resize: 'vertical' }} />
            : <input type={type} value={form[k]} onChange={e => set(k, e.target.value)}
                style={inp({ height: 38 })} />
        }
    </div>
);

const BusinessSection = ({ config, onToast, onReload }) => {
    const { refreshTenantConfig } = useAuth();
    const { t } = useLanguage();
    const [form, setForm] = useState({
        brand_name:             config?.brand_name || '',
        long_brand_name:        config?.long_brand_name || '',
        currency:               config?.currency || '£',
        tax_rate:               config?.tax_rate ?? 0,
        service_charge_rate:    config?.service_charge_rate ?? 0,
        receipt_header:         config?.receipt_header || '',
        receipt_footer:         config?.receipt_footer || '',
        address:                config?.address || '',
        phone:                  config?.phone || '',
        email:                  config?.email || '',
        website:                config?.website || '',
        social_instagram:       config?.social_media?.instagram || '',
        social_facebook:        config?.social_media?.facebook || '',
        social_twitter:         config?.social_media?.twitter || '',
        social_tiktok:          config?.social_media?.tiktok || '',
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const socialMedia = {
                instagram: form.social_instagram,
                facebook:  form.social_facebook,
                twitter:   form.social_twitter,
                tiktok:    form.social_tiktok,
            };
            await tenantService.updateBasicSettings({
                brand_name:          form.brand_name,
                long_brand_name:     form.long_brand_name,
                currency:            form.currency,
                tax_rate:            parseFloat(form.tax_rate) || 0,
                service_charge_rate: parseFloat(form.service_charge_rate) || 0,
                receipt_header:      form.receipt_header,
                receipt_footer:      form.receipt_footer,
                address:             form.address,
                phone:               form.phone,
                email:               form.email,
                website:             form.website,
                social_media:        socialMedia,
            });
            if (refreshTenantConfig) await refreshTenantConfig();
            onToast('success', t('business_settings_saved') || 'Business settings saved');
            setTimeout(() => window.location.reload(), 900);
        } catch {
            onToast('error', t('failed_to_save_settings') || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    // Shared by every Field below, so the hoisted component can still read and
    // write this section's form state.
    const f = { form, set };

    return (
        <div>
            <SectionHeader
                title={t('business') || 'Business'}
                description={t('business_section_description') || 'Your brand identity, contact information, and receipt configuration. Changes take effect immediately after saving.'}
            />

            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', maxWidth: 780 }}>
                <Field {...f} label={t('brand_name_short') || 'Brand Name (short)'}  k="brand_name"       half />
                <Field {...f} label={t('brand_name_full') || 'Brand Name (full)'}   k="long_brand_name"  half />
                <Field {...f} label={t('currency_symbol') || 'Currency Symbol'}     k="currency" />

                <div style={{ gridColumn: 'span 1' }}>
                    <label style={labelStyle}>{t('tax_rate') || 'Tax Rate (0–1)'}</label>
                    <input type="number" step="0.01" min="0" max="1"
                        value={form.tax_rate} onChange={e => set('tax_rate', e.target.value)}
                        style={inp({ height: 38 })} />
                </div>
                <div style={{ gridColumn: 'span 1' }}>
                    <label style={labelStyle}>{t('service_charge_rate') || 'Service Charge Rate (0–1)'}</label>
                    <input type="number" step="0.01" min="0" max="1"
                        value={form.service_charge_rate} onChange={e => set('service_charge_rate', e.target.value)}
                        style={inp({ height: 38 })} />
                </div>

                <G2><SubLabel mt={8}>{t('receipt') || 'Receipt'}</SubLabel></G2>
                <Field {...f} label={t('receipt_header') || 'Receipt Header'} k="receipt_header" half />
                <Field {...f} label={t('receipt_footer') || 'Receipt Footer'}  k="receipt_footer" half />

                <G2><SubLabel mt={8}>{t('contact') || 'Contact'}</SubLabel></G2>
                <Field {...f} label={t('address') || 'Address'}  k="address"  textarea />
                <Field {...f} label={t('phone') || 'Phone'}    k="phone"    half />
                <Field {...f} label={t('email') || 'Email'}    k="email"    half type="email" />
                <Field {...f} label={t('website') || 'Website'}  k="website" />

                <G2><SubLabel mt={8}>{t('social_media') || 'Social Media'}</SubLabel></G2>
                <Field {...f} label="Instagram"   k="social_instagram" half />
                <Field {...f} label="Facebook"    k="social_facebook"  half />
                <Field {...f} label="X (Twitter)" k="social_twitter"   half />
                <Field {...f} label="TikTok"      k="social_tiktok"    half />
            </div>

            <div style={{ marginTop: 28 }}>
                <SaveBtn onClick={handleSave} saving={saving} t={t} />
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Sales
// ═════════════════════════════════════════════════════════════════════════════
// ─── Translate icon SVG (for SalesSection) ──────────────────────────────────
const TranslateIcon = ({ size = 14, color = C.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 8l6 0" />
        <path d="M4 14c.8-2.4 2.5-5 4-6" />
        <path d="M8 14c-.8-2.4-2.5-5-4-6" />
        <path d="M13 4l3 8 3-8" />
        <path d="M13 12h6" />
    </svg>
);

// ─── Translation Modal (for SalesSection entities) ──────────────────────────
const SalesTranslationModal = ({ name, currentMode, currentManual, onSave, onClose }) => {
    const { t } = useLanguage();
    const [mode, setMode] = useState(currentMode || 'translation');
    const [manualText, setManualText] = useState('');
    const [aiResult, setAiResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (currentManual) {
            try {
                const parsed = typeof currentManual === 'string' ? JSON.parse(currentManual) : currentManual;
                setManualText(parsed?.ar || '');
            } catch {
                setManualText(currentManual || '');
            }
        }
    }, [currentManual]);

    useEffect(() => {
        if ((mode === 'translation' || mode === 'transliteration') && name) {
            setLoading(true);
            translationService.translateProductName(name, 'entity')
                .then(result => setAiResult(result))
                .catch(() => setAiResult(null))
                .finally(() => setLoading(false));
        }
    }, [mode, name]);

    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose]);

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = { translation_mode: mode };
            if (mode === 'manual') {
                data.manual_translation = JSON.stringify({ ar: manualText });
            }
            await onSave(data);
        } finally {
            setSaving(false);
        }
    };

    const displayResult = mode === 'translation' ? aiResult?.translation : aiResult?.transliteration;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.4)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
        }} onClick={onClose}>
            <div className="portal-modal-card" style={{
                background: C.white, borderRadius: 14, padding: 28, width: 420,
                maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h3 style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text, margin: 0 }}>
                        {t('translate')}: {name}
                    </h3>
                    <button onClick={onClose} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: C.muted,
                        padding: 4, display: 'flex',
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <SubLabel mt={0}>{t('translation_mode')}</SubLabel>
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    {[
                        { key: 'translation', label: t('translation_ai') },
                        { key: 'transliteration', label: t('transliteration') },
                        { key: 'manual', label: t('manual') },
                    ].map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => setMode(opt.key)}
                            style={{
                                flex: 1, padding: '8px 12px', borderRadius: 8,
                                border: `1.5px solid ${mode === opt.key ? C.green : C.border}`,
                                background: mode === opt.key ? C.greenLight : C.white,
                                color: mode === opt.key ? C.green : C.muted,
                                fontFamily: FONT, fontSize: 12, fontWeight: 600,
                                cursor: 'pointer', transition: 'all 0.15s',
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>

                {mode === 'manual' ? (
                    <div>
                        <SubLabel mt={0}>{t('arabic_translation')}</SubLabel>
                        <input
                            value={manualText}
                            onChange={e => setManualText(e.target.value)}
                            placeholder={t('enter_arabic_translation')}
                            dir="rtl"
                            style={inp({ marginBottom: 16 })}
                        />
                    </div>
                ) : (
                    <div style={{
                        padding: 16, borderRadius: 10,
                        background: C.bg, border: `1.5px solid ${C.border}`,
                        marginBottom: 16, minHeight: 48,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {loading ? (
                            <span style={{ fontFamily: FONT, fontSize: 13, color: C.muted }}>{t('translating')}...</span>
                        ) : displayResult ? (
                            <span style={{ fontFamily: FONT, fontSize: 18, color: C.text, direction: 'rtl' }}>
                                {displayResult}
                            </span>
                        ) : (
                            <span style={{ fontFamily: FONT, fontSize: 13, color: C.mutedLight }}>
                                {t('no_translation_available')}
                            </span>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{
                        padding: '8px 18px', borderRadius: 8,
                        border: `1.5px solid ${C.border}`, background: C.white,
                        cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.muted,
                    }}>{t('cancel')}</button>
                    <button onClick={handleSave} disabled={saving} style={{
                        padding: '8px 18px', borderRadius: 8, border: 'none',
                        background: C.green, color: C.white,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        opacity: saving ? 0.7 : 1,
                    }}>{saving ? t('saving') + '...' : t('save')}</button>
                </div>
            </div>
        </div>
    );
};

const SalesSection = ({ config, onToast, onReload }) => {
    const { t } = useLanguage();
    const { hasFeature, getLimit, limits } = useAuth();
    const [paymentMethods,    setPaymentMethods]    = useState([]);
    const [diningOptions,     setDiningOptions]     = useState([]);
    const [serviceApplied,    setServiceApplied]    = useState([]);
    const [taxApplied,        setTaxApplied]        = useState(config?.tax_applied         || []);
    const [newPayment,        setNewPayment]        = useState('');
    const [diningModal,       setDiningModal]       = useState(null); // null | 'add' | { editing: true, id, name, ... }
    const [layoutModal,       setLayoutModal]       = useState(null); // null | dining option object
    const [translateModal,    setTranslateModal]    = useState(null); // null | { type, id, name, mode, manual }
    const [loadingData,       setLoadingData]       = useState(true);

    // ── Fetch data on mount ──────────────────────────────────────────────────
    useEffect(() => {
        const load = async () => {
            try {
                const [pmData, doData] = await Promise.all([
                    paymentMethodService.getAll(),
                    diningOptionService.getAll(),
                ]);
                setPaymentMethods(pmData || []);
                const sortedDo = (doData || []).sort((a, b) => a.name.localeCompare(b.name));
                setDiningOptions(sortedDo);
                setServiceApplied(sortedDo.filter(o => o.service_charge_applied).map(o => o.name));
            } catch {
                onToast('error', t('failed_to_load_data'));
            } finally {
                setLoadingData(false);
            }
        };
        load();
    }, []);

    // ── Payment Methods ──────────────────────────────────────────────────────
    const addPayment = async () => {
        const v = newPayment.trim();
        if (!v || paymentMethods.some(m => m.name === v)) return;
        try {
            const created = await paymentMethodService.create({ name: v });
            setPaymentMethods(prev => [...prev, created]);
            setNewPayment('');
            onToast('success', `"${v}" ${t('added')}`);
        } catch { onToast('error', t('failed_to_add_payment_method')); }
    };

    const removePayment = async (method) => {
        if (method.is_protected) { onToast('error', t('core_payment_method_cannot_be_removed')); return; }
        try {
            await paymentMethodService.delete(method.id);
            setPaymentMethods(prev => prev.filter(m => m.id !== method.id));
            onToast('success', `"${method.name}" ${t('removed')}`);
        } catch { onToast('error', t('failed_to_remove_payment_method')); }
    };

    const savePaymentTranslation = async (data) => {
        if (!translateModal) return;
        try {
            const updated = await paymentMethodService.update(translateModal.id, data);
            setPaymentMethods(prev => prev.map(m => m.id === updated.id ? updated : m));
            setTranslateModal(null);
            onToast('success', t('translation_saved'));
        } catch { onToast('error', t('failed_to_save_translation')); }
    };

    // ── Dining Options ───────────────────────────────────────────────────────
    const buildTablesConfig = (tableType, tableCount, existing) => {
        if (tableType === 'no_tables') return null;
        if (tableType === 'random_number') return 'random_number';
        const count = parseInt(tableCount) || 1;
        // Convert from legacy array-of-objects to new JSON object format if needed
        let existingObj = {};
        if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
            existingObj = existing;
        } else if (Array.isArray(existing) && existing.length > 0 && typeof existing[0] === 'object' && existing[0].number !== undefined) {
            // Migrate legacy format: [{number:1, x:..., y:...}] → {"1": [{x:..., y:...}]}
            existing.forEach(t => {
                const { number, ...design } = t;
                existingObj[String(number)] = Object.keys(design).length > 0 ? [design] : [];
            });
        }
        const result = {};
        for (let i = 1; i <= count; i++) {
            const key = String(i);
            if (existingObj[key]) {
                result[key] = existingObj[key];
            } else {
                // Default empty design
                result[key] = [];
            }
        }
        return result;
    };

    const saveDiningOption = async ({ name, tableType, tableCount }) => {
        const isEditing = diningModal?.editing;
        try {
            if (isEditing) {
                const existingOption = diningOptions.find(o => o.id === diningModal.id);
                const tablesConfig = buildTablesConfig(tableType, tableCount, existingOption?.tables_config);
                const updated = await diningOptionService.update(diningModal.id, { name, tables_config: tablesConfig });
                setDiningOptions(prev => prev.map(o => o.id === updated.id ? updated : o));
                onToast('success', t('dining_option_updated'));
            } else {
                const tablesConfig = buildTablesConfig(tableType, tableCount, null);
                const created = await diningOptionService.create({ name, tables_config: tablesConfig });
                setDiningOptions(prev => [...prev, created]);
                onToast('success', t('dining_option_added'));
            }
            setDiningModal(null);
        } catch { onToast('error', t('failed_to_save_dining_option')); }
    };

    const removeDiningOption = async (option) => {
        try {
            await diningOptionService.delete(option.id);
            setDiningOptions(prev => prev.filter(o => o.id !== option.id));
            onToast('success', `"${option.name}" ${t('removed')}`);
        } catch { onToast('error', t('failed_to_remove_dining_option')); }
    };

    const saveTableLayout = async (option, updatedTables) => {
        try {
            const updated = await diningOptionService.update(option.id, { tables_config: updatedTables });
            setDiningOptions(prev => prev.map(o => o.id === updated.id ? updated : o));
            setLayoutModal(null);
            onToast('success', t('table_layout_saved'));
        } catch { onToast('error', t('failed_to_save_layout')); }
    };

    const saveDiningTranslation = async (data) => {
        if (!translateModal) return;
        try {
            const updated = await diningOptionService.update(translateModal.id, data);
            setDiningOptions(prev => prev.map(o => o.id === updated.id ? updated : o));
            setTranslateModal(null);
            onToast('success', t('translation_saved'));
        } catch { onToast('error', t('failed_to_save_translation')); }
    };

    const getTableCount = (val) => {
        if (!val) return null;
        if (val === 'random_number') return null;
        if (Array.isArray(val)) {
            if (val.length === 1 && val[0] === 'random_number') return null;
            return val.filter(item => item && typeof item === 'object' && 'number' in item).length;
        }
        if (typeof val === 'object') return Object.keys(val).length;
        return null;
    };

    const getTableTypeLabel = (val) => {
        if (!val || val === '' || val === null) return t('no_tables');
        if (val === 'random_number') return t('any_table_number');
        if (Array.isArray(val)) {
            if (val.length === 1 && val[0] === 'random_number') return t('any_table_number');
            const count = getTableCount(val);
            if (count === 0) return t('no_tables');
            return `${count} ${count !== 1 ? t('tables') : t('table')}`;
        }
        if (typeof val === 'object') {
            const count = Object.keys(val).length;
            if (count === 0) return t('no_tables');
            return `${count} ${count !== 1 ? t('tables') : t('table')}`;
        }
        return t('unknown');
    };

    if (loadingData) {
        return (
            <div style={{ fontFamily: FONT, fontSize: 14, color: C.muted, paddingTop: 60, textAlign: 'center' }}>
                {t('loading')}...
            </div>
        );
    }

    return (
        <div>
            {/* Translation Modal */}
            {translateModal && (
                <SalesTranslationModal
                    name={translateModal.name}
                    currentMode={translateModal.mode}
                    currentManual={translateModal.manual}
                    onSave={translateModal.type === 'payment' ? savePaymentTranslation : saveDiningTranslation}
                    onClose={() => setTranslateModal(null)}
                />
            )}

            {/* Dining Option Modal */}
            {diningModal && (
                <DiningOptionModal
                    editing={diningModal?.editing || false}
                    initial={diningModal?.editing ? {
                        name: diningModal.name,
                        tableType: diningModal.tableType,
                        tableCount: diningModal.tableCount,
                    } : undefined}
                    onSave={saveDiningOption}
                    onClose={() => setDiningModal(null)}
                />
            )}

            {/* Table Layout Modal */}
            {layoutModal && (
                <TableLayoutModal
                    diningOptionName={layoutModal.name}
                    tables={(() => {
                        const tc = layoutModal.tables_config;
                        if (Array.isArray(tc)) return tc;
                        if (tc && typeof tc === 'object') {
                            // Convert {"1": [design], "2": []} → [{number:1, ...design}, ...]
                            return Object.entries(tc).map(([num, design], index) => {
                                const d = Array.isArray(design) && design.length > 0 ? design[0] : {};
                                return {
                                    number: parseInt(num),
                                    x: d.x ?? +(0.1 + ((index % 5) * 0.18)).toFixed(4),
                                    y: d.y ?? +(0.15 + (Math.floor(index / 5) * 0.25)).toFixed(4),
                                    shape: d.shape || 'circle',
                                    color: d.color || '#3B82F6',
                                    size: d.size || 60,
                                };
                            }).sort((a, b) => a.number - b.number);
                        }
                        return [];
                    })()}
                    onSave={(updatedTables) => {
                        // Convert [{number:1, x, y, ...}] → {"1": [{x, y, ...}], "2": [...]}
                        const newConfig = {};
                        updatedTables.forEach(t => {
                            const { number, ...design } = t;
                            newConfig[String(number)] = Object.keys(design).length > 0 ? [design] : [];
                        });
                        saveTableLayout(layoutModal, newConfig);
                    }}
                    onClose={() => setLayoutModal(null)}
                />
            )}

            <SectionHeader
                title={t('sales')}
                description={t('sales_section_description')}
            />

            {/* Payment Methods */}
            <SubLabel>{t('payment_methods')}</SubLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {paymentMethods.map(method => (
                    <span key={method.id} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '5px 12px 5px 14px', borderRadius: 8,
                        background: method.is_protected ? C.bg : C.greenLight,
                        border: `1.5px solid ${method.is_protected ? C.border : C.greenBorder}`,
                        fontFamily: FONT, fontSize: 13, fontWeight: 600,
                        color: method.is_protected ? C.muted : C.green,
                    }}>
                        {method.name}
                        {method.translation_mode && (
                            <span style={{ fontFamily: FONT, fontSize: 9, color: C.mutedLight, marginLeft: 2 }}>
                                {method.translation_mode}
                            </span>
                        )}
                        <button
                            onClick={() => setTranslateModal({
                                type: 'payment', id: method.id, name: method.name,
                                mode: method.translation_mode, manual: method.manual_translation,
                            })}
                            title={t('translate')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 3px', display: 'flex', alignItems: 'center' }}
                        >
                            <TranslateIcon size={13} color={method.translation_mode ? C.green : C.mutedLight} />
                        </button>
                        {!method.is_protected && <DelBtn onClick={() => removePayment(method)} />}
                    </span>
                ))}
            </div>
            {!hasFeature('custom_payment_methods') ? (
                <div style={{ marginBottom: 32 }}>
                    <LimitReached requiredPlan="Pro" />
                    <p style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 6, marginBottom: 0 }}>
                        {t('custom_payment_methods_locked_hint')}
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', gap: 8, maxWidth: 360, marginBottom: 32 }}>
                    <input
                        value={newPayment}
                        onChange={e => setNewPayment(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addPayment()}
                        placeholder={t('new_payment_method_placeholder')}
                        style={inp({ height: 38, flex: 1 })}
                    />
                    <button onClick={addPayment} style={{
                        padding: '0 14px', height: 38, borderRadius: 8,
                        background: C.green, color: C.white, border: 'none',
                        cursor: 'pointer', fontFamily: FONT, fontSize: 12, fontWeight: 700,
                    }}>{t('add')}</button>
                </div>
            )}

            {/* Dining Options */}
            <SubLabel>{t('dining_options')}</SubLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, maxWidth: 780, marginBottom: 12 }}>
                {diningOptions.map(option => {
                    const val = option.tables_config;
                    const label = getTableTypeLabel(val);
                    const hasLayout = (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0] !== null && 'number' in val[0])
                        || (val && typeof val === 'object' && !Array.isArray(val) && val !== null && Object.keys(val).length > 0);
                    return (
                        <div key={option.id} style={{
                            background: C.white, border: `1.5px solid ${C.border}`,
                            borderRadius: 10, padding: 14,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{option.name}</div>
                                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                    <button
                                        onClick={() => setTranslateModal({
                                            type: 'dining', id: option.id, name: option.name,
                                            mode: option.translation_mode, manual: option.manual_translation,
                                        })}
                                        title={t('translate')}
                                        style={{ padding: '3px 6px', borderRadius: 6, border: `1.5px solid ${C.border}`, background: C.bg, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                    >
                                        <TranslateIcon size={13} color={option.translation_mode ? C.green : C.muted} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            const tc = option.tables_config;
                                            let tType = 'no_tables', tCount = 5;
                                            if (tc === 'random_number') {
                                                tType = 'random_number';
                                            } else if (Array.isArray(tc) && tc.length === 1 && tc[0] === 'random_number') {
                                                tType = 'random_number';
                                            } else if (Array.isArray(tc) && tc.length > 0) {
                                                tType = 'tables'; tCount = getTableCount(tc);
                                            } else if (tc && typeof tc === 'object' && !Array.isArray(tc)) {
                                                tType = 'tables'; tCount = Object.keys(tc).length;
                                            }
                                            setDiningModal({ editing: true, id: option.id, name: option.name, tableType: tType, tableCount: tCount });
                                        }}
                                        style={{ padding: '3px 8px', borderRadius: 6, border: `1.5px solid ${C.border}`, background: C.bg, cursor: 'pointer', fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}
                                    >{t('edit')}</button>
                                    <DelBtn onClick={() => removeDiningOption(option)} />
                                </div>
                            </div>
                            <div style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginBottom: 0 }}>{label}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}>
                                        {t('service_charge')} ({Math.round((config?.service_charge_rate || 0) * 100)}%)
                                    </span>
                                    <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={serviceApplied.includes(option.name)}
                                            onChange={async () => {
                                                const newVal = !serviceApplied.includes(option.name);
                                                const updated = newVal
                                                    ? [...serviceApplied, option.name]
                                                    : serviceApplied.filter(n => n !== option.name);
                                                setServiceApplied(updated);
                                                try {
                                                    await diningOptionService.update(option.id, { service_charge_applied: newVal });
                                                    onReload();
                                                    onToast('success', t('updated'));
                                                } catch { onToast('error', t('failed_to_update')); }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                        />
                                        <span style={{
                                            position: 'absolute', inset: 0, borderRadius: 10,
                                            background: serviceApplied.includes(option.name) ? C.green : '#cbd5e1',
                                            transition: 'background 0.2s',
                                        }}>
                                            <span style={{
                                                position: 'absolute', top: 2, left: serviceApplied.includes(option.name) ? 18 : 2,
                                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }} />
                                        </span>
                                    </label>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}>
                                        {t('tax')} ({Math.round((config?.tax_rate || 0) * 100)}%)
                                    </span>
                                    <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={taxApplied.includes(option.name)}
                                            onChange={async () => {
                                                const updated = taxApplied.includes(option.name)
                                                    ? taxApplied.filter(n => n !== option.name)
                                                    : [...taxApplied, option.name];
                                                setTaxApplied(updated);
                                                try {
                                                    await tenantService.updateTenantConfig({ tax_applied: updated });
                                                    onReload();
                                                    onToast('success', t('updated'));
                                                } catch { onToast('error', t('failed_to_update')); }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                        />
                                        <span style={{
                                            position: 'absolute', inset: 0, borderRadius: 10,
                                            background: taxApplied.includes(option.name) ? C.green : '#cbd5e1',
                                            transition: 'background 0.2s',
                                        }}>
                                            <span style={{
                                                position: 'absolute', top: 2, left: taxApplied.includes(option.name) ? 18 : 2,
                                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }} />
                                        </span>
                                    </label>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}>
                                            {t('show_service_charge_toggle')}
                                        </span>
                                        <div
                                            title={t('show_service_charge_toggle_tooltip')}
                                            style={{ width: 14, height: 14, borderRadius: '50%', border: `1px solid ${C.mutedLight}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', fontSize: 9, color: C.muted, flexShrink: 0 }}
                                        >
                                            i
                                        </div>
                                    </div>
                                    <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            data-testid="svc-toggle-visible"
                                            checked={option.service_charge_toggle_visible !== false}
                                            onChange={async (e) => {
                                                const newVal = e.target.checked;
                                                setDiningOptions(prev => prev.map(o => o.id === option.id ? { ...o, service_charge_toggle_visible: newVal } : o));
                                                try {
                                                    await diningOptionService.update(option.id, { service_charge_toggle_visible: newVal });
                                                    onToast('success', t('updated'));
                                                } catch { onToast('error', t('failed_to_update')); }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                        />
                                        <span style={{
                                            position: 'absolute', inset: 0, borderRadius: 10,
                                            background: option.service_charge_toggle_visible !== false ? C.green : '#cbd5e1',
                                            transition: 'background 0.2s',
                                        }}>
                                            <span style={{
                                                position: 'absolute', top: 2, left: option.service_charge_toggle_visible !== false ? 18 : 2,
                                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }} />
                                        </span>
                                    </label>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}>
                                            {t('price_tier')}
                                        </span>
                                        <div
                                            title={t('price_tier_tooltip')}
                                            style={{ width: 14, height: 14, borderRadius: '50%', border: `1px solid ${C.mutedLight}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', fontSize: 9, color: C.muted, flexShrink: 0 }}
                                        >
                                            i
                                        </div>
                                    </div>
                                    <select
                                        data-testid="price-tier"
                                        value={option.price_tier || 'primary'}
                                        onChange={async (e) => {
                                            const newVal = e.target.value;
                                            setDiningOptions(prev => prev.map(o => o.id === option.id ? { ...o, price_tier: newVal } : o));
                                            try {
                                                await diningOptionService.update(option.id, { price_tier: newVal });
                                                onToast('success', t('updated'));
                                            } catch { onToast('error', t('failed_to_update')); }
                                        }}
                                        style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 6px', background: C.white }}
                                    >
                                        <option value="primary">{t('primary_price')}</option>
                                        <option value="secondary">{t('secondary_price')}</option>
                                    </select>
                                </div>

                                {hasFeature('table_service') && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: C.muted }}>
                                            {t('auto_clear_on_payment') || 'Auto-clear on payment'}
                                        </span>
                                        <div
                                            title={t('auto_clear_tooltip') || 'Automatically clear the table when payment is completed'}
                                            style={{ width: 14, height: 14, borderRadius: '50%', border: `1px solid ${C.mutedLight}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', fontSize: 9, color: C.muted, flexShrink: 0 }}
                                        >
                                            i
                                        </div>
                                    </div>
                                    <label style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={option.auto_clear_on_payment !== false}
                                            onChange={async (e) => {
                                                const newVal = e.target.checked;
                                                setDiningOptions(prev => prev.map(o => o.id === option.id ? { ...o, auto_clear_on_payment: newVal } : o));
                                                try {
                                                    await diningOptionService.update(option.id, { auto_clear_on_payment: newVal });
                                                    onToast('success', t('updated'));
                                                } catch { onToast('error', t('failed_to_update')); }
                                            }}
                                            style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                                        />
                                        <span style={{
                                            position: 'absolute', inset: 0, borderRadius: 10,
                                            background: option.auto_clear_on_payment !== false ? C.green : '#cbd5e1',
                                            transition: 'background 0.2s',
                                        }}>
                                            <span style={{
                                                position: 'absolute', top: 2, left: option.auto_clear_on_payment !== false ? 18 : 2,
                                                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                            }} />
                                        </span>
                                    </label>
                                </div>
                                )}
                            </div>
                            {hasLayout && hasFeature('table_service') && (
                                <button
                                    onClick={() => setLayoutModal(option)}
                                    style={{
                                        width: '100%', padding: '6px 0', borderRadius: 7, marginTop: 8,
                                        border: `1.5px solid ${C.greenBorder}`, background: C.greenLight,
                                        cursor: 'pointer', fontFamily: FONT, fontSize: 11, fontWeight: 700,
                                        color: C.green,
                                    }}
                                >
                                    {t('design_layout')}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
            {(() => {
                const diningCap = getLimit('max_dining_options');
                const diningFull = atLimit(limits, 'max_dining_options', diningOptions.length);
                return (
                    <>
                        <button
                            onClick={() => !diningFull && setDiningModal('add')}
                            disabled={diningFull}
                            style={{
                                padding: '8px 18px', borderRadius: 8, border: `1.5px dashed ${diningFull ? C.border : C.greenBorder}`,
                                background: diningFull ? C.bg : C.greenLight,
                                cursor: diningFull ? 'not-allowed' : 'pointer',
                                fontFamily: FONT, fontSize: 13, fontWeight: 700,
                                color: diningFull ? C.muted : C.green,
                                opacity: diningFull ? 0.7 : 1,
                            }}
                        >
                            + {t('add_dining_option')}
                        </button>
                        {diningFull && (
                            <div style={{ marginTop: 8 }}>
                                <LimitReached cap={diningCap} requiredPlan="Pro" />
                            </div>
                        )}
                    </>
                );
            })()}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Team & Expenses
// ═════════════════════════════════════════════════════════════════════════════
const TeamSection = ({ config, onToast }) => {
    const { t } = useLanguage();
    const { hasFeature } = useAuth();
    const [stations,    setStations]    = useState(config?.stations                  || DEFAULT_STATIONS);
    const [departments, setDepartments] = useState(config?.departments               || DEFAULT_DEPARTMENTS);
    const [dailyCats,   setDailyCats]   = useState(config?.daily_expense_categories  || DEFAULT_DAILY_CATS);
    const [ohCats,      setOhCats]      = useState(config?.overhead_expense_categories || DEFAULT_OH_CATS);

    const save = async (key, value, setter) => {
        try {
            await tenantService.updateTenantConfig({ [key]: value });
            setter(value);
            onToast('success', t('saved') || 'Saved');
        } catch { onToast('error', t('failed_to_save') || 'Failed to save'); }
    };

    const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };

    return (
        <div>
            <SectionHeader
                title={t('team_and_expenses') || 'Team & Expenses'}
                description={t('team_section_description') || 'Manage departments and stations used for staff scheduling, and configure expense categories for the daily and overhead expense trackers.'}
            />

            <GroupCard
                icon={<IconTeamGroup />}
                title={t('team') || 'Team'}
                description={t('team_group_description') || 'Structure used across employee profiles and the schedule.'}
            >
                <LockOverlay locked={!hasFeature('hr')} requiredPlan="Pro">
                    <div className="portal-grid-2" style={gridStyle}>
                        <ListPanel
                            title={t('departments') || 'Departments'}
                            description={t('departments_description') || 'Used in employee profiles and filtering.'}
                            items={departments}
                            placeholder={t('placeholder_department')}
                            onAdd={(v) => save('departments', [...departments, v], setDepartments)}
                            onRemove={(v) => save('departments', departments.filter(x => x !== v), setDepartments)}
                        />
                        <ListPanel
                            title={t('shift_stations') || 'Shift Stations'}
                            description={t('shift_stations_description') || 'Predefined stations for the schedule page.'}
                            items={stations}
                            placeholder={t('placeholder_station')}
                            locked={DEFAULT_STATIONS}
                            onAdd={(v) => save('stations', [...stations, v], setStations)}
                            onRemove={(v) => save('stations', stations.filter(x => x !== v), setStations)}
                        />
                    </div>
                </LockOverlay>
            </GroupCard>

            <GroupCard
                icon={<IconExpensesGroup />}
                title={t('expenses') || 'Expenses'}
                description={t('expense_categories_group_description') || 'Categories for the daily and overhead expense trackers.'}
            >
                <LockOverlay locked={!hasFeature('expenses')} requiredPlan="Pro">
                    <div className="portal-grid-2" style={gridStyle}>
                        <ListPanel
                            title={t('daily_expense_categories') || 'Daily Expense Categories'}
                            description={t('daily_expense_categories_description') || 'Categories for day-to-day operational expenses.'}
                            items={dailyCats}
                            placeholder={t('placeholder_daily_expense')}
                            onAdd={(v) => save('daily_expense_categories', [...dailyCats, v], setDailyCats)}
                            onRemove={(v) => save('daily_expense_categories', dailyCats.filter(x => x !== v), setDailyCats)}
                        />
                        <ListPanel
                            title={t('overhead_categories') || 'Overhead Categories'}
                            description={t('overhead_categories_description') || 'Categories for recurring fixed costs.'}
                            items={ohCats}
                            placeholder={t('placeholder_overhead_expense')}
                            onAdd={(v) => save('overhead_expense_categories', [...ohCats, v], setOhCats)}
                            onRemove={(v) => save('overhead_expense_categories', ohCats.filter(x => x !== v), setOhCats)}
                        />
                    </div>
                </LockOverlay>
            </GroupCard>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Appearance
// ═════════════════════════════════════════════════════════════════════════════
// Module scope for the same reason as Field above: declared inside
// AppearanceSection it was remounted on every change, which recreated the
// <input type="color"> and interrupted the native colour picker mid-drag.
const ColorField = ({ label, k, theme, setT }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input
            type="color"
            value={theme[k]}
            onChange={e => setT(k, e.target.value)}
            style={{ width: 36, height: 36, borderRadius: 8, border: `1.5px solid ${C.border}`, cursor: 'pointer', padding: 2 }}
        />
        <div>
            <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: C.muted }}>{theme[k]}</div>
        </div>
    </div>
);

const AppearanceSection = ({ config, onToast, onReload, setReceiptTranslationSettings }) => {
    const { refreshTenantConfig } = useAuth();
    const { t } = useLanguage();
    const [theme, setTheme] = useState({
        primary_color:     config?.theme?.primary_color     || '#0ea5e9',
        secondary_color:   config?.theme?.secondary_color   || '#0284c7',
        accent_color:      config?.theme?.accent_color      || '#38bdf8',
        text_color:        config?.theme?.text_color        || '#0c4a6e',
        background_color:  config?.theme?.background_color  || '#f0f9ff',
        card_selected:     config?.theme?.card_selected_color || '#dbeafe',
        card_hover:        config?.theme?.card_hover_color   || '#f3f4f6',
        card_normal:       config?.theme?.card_normal_color  || '#ffffff',
    });
    const [saving, setSaving] = useState(false);

    const [receiptEnabled, setReceiptEnabled] = useState(
        localStorage.getItem('receiptTranslationEnabled') === 'true'
    );
    const [receiptLang, setReceiptLang] = useState(
        localStorage.getItem('receiptTranslationLanguage') || ''
    );
    const availableLangs = config?.languages || ['en'];

    const setT = (k, v) => setTheme(p => ({ ...p, [k]: v }));

    const resetTheme = () => setTheme({
        primary_color: '#0ea5e9', secondary_color: '#0284c7', accent_color: '#38bdf8',
        text_color: '#0c4a6e', background_color: '#f0f9ff', card_selected: '#dbeafe',
        card_hover: '#f3f4f6', card_normal: '#ffffff',
    });

    const saveTheme = async () => {
        setSaving(true);
        try {
            await tenantService.updateTenantConfig({
                theme: {
                    primary_color:      theme.primary_color,
                    secondary_color:    theme.secondary_color,
                    accent_color:       theme.accent_color,
                    text_color:         theme.text_color,
                    background_color:   theme.background_color,
                    card_selected_color: theme.card_selected,
                    card_hover_color:   theme.card_hover,
                    card_normal_color:  theme.card_normal,
                },
            });
            if (refreshTenantConfig) await refreshTenantConfig();
            onToast('success', t('theme_saved') || 'Theme saved — reloading...');
            setTimeout(() => window.location.reload(), 900);
        } catch { onToast('error', t('failed_to_save_theme') || 'Failed to save theme'); }
        finally { setSaving(false); }
    };

    const saveReceiptSettings = () => {
        if (setReceiptTranslationSettings) {
            setReceiptTranslationSettings(receiptEnabled, receiptLang);
        }
        onToast('success', t('receipt_translation_settings_saved') || 'Receipt translation settings saved');
    };

    // Shared by every ColorField below — see the module-scope component above.
    const c = { theme, setT };

    return (
        <div>
            <SectionHeader
                title={t('appearance') || 'Appearance'}
                description={t('appearance_section_description') || 'Customise the colour theme for the POS and cashier views. These settings do not affect the manager portal.'}
            />

            {/* Logo first: it is the one thing on this page a customer ever
                sees, and until now changing it meant raising a support
                ticket. Colours affect only the till. */}
            <div style={{ marginBottom: 40 }}>
                <LogoUploader config={config} onToast={onToast} onSaved={onReload} />
            </div>

            <SubLabel>{t('pos_theme_colors') || 'POS Theme Colors'}</SubLabel>
            <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 560, marginBottom: 20 }}>
                <ColorField {...c} label={t('primary_color') || 'Primary Color'}     k="primary_color" />
                <ColorField {...c} label={t('secondary_color') || 'Secondary Color'}   k="secondary_color" />
                <ColorField {...c} label={t('accent_color') || 'Accent Color'}      k="accent_color" />
                <ColorField {...c} label={t('text_color') || 'Text Color'}        k="text_color" />
                <ColorField {...c} label={t('background_color') || 'Background Color'}  k="background_color" />
                <ColorField {...c} label={t('card_selected') || 'Card Selected'}     k="card_selected" />
                <ColorField {...c} label={t('card_hover') || 'Card Hover'}        k="card_hover" />
                <ColorField {...c} label={t('card_normal') || 'Card Normal'}       k="card_normal" />
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 40 }}>
                <SaveBtn onClick={saveTheme} saving={saving} t={t} />
                <button onClick={resetTheme} style={{
                    padding: '9px 18px', borderRadius: 9, border: `1.5px solid ${C.border}`,
                    background: C.bg, cursor: 'pointer',
                    fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.muted,
                }}>
                    {t('reset_to_defaults') || 'Reset to Defaults'}
                </button>
            </div>

            <SubLabel>{t('receipt_translation') || 'Receipt Translation'}</SubLabel>
            <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: 20, maxWidth: 480 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: receiptEnabled ? 16 : 0 }}>
                    <div>
                        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.text }}>{t('enable_receipt_translation') || 'Enable Receipt Translation'}</div>
                        <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 2 }}>{t('translate_receipts_description') || 'Translate printed receipts to another language'}</div>
                    </div>
                    <button
                        onClick={() => setReceiptEnabled(p => !p)}
                        style={{
                            width: 44, height: 24, borderRadius: 12,
                            background: receiptEnabled ? C.green : C.border,
                            border: 'none', cursor: 'pointer', position: 'relative',
                            transition: 'background 0.2s', flexShrink: 0,
                        }}
                    >
                        <span style={{
                            position: 'absolute', top: 4,
                            left: receiptEnabled ? 22 : 4,
                            width: 16, height: 16, borderRadius: '50%',
                            background: C.white, transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                        }} />
                    </button>
                </div>

                {receiptEnabled && (
                    <div style={{ marginTop: 14 }}>
                        <label style={labelStyle}>{t('translation_language') || 'Translation Language'}</label>
                        <select
                            value={receiptLang}
                            onChange={e => setReceiptLang(e.target.value)}
                            style={inp({ height: 38 })}
                        >
                            <option value="">{t('select_language') || 'Select language...'}</option>
                            {availableLangs.map(lang => (
                                <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                            ))}
                        </select>
                    </div>
                )}

                <button onClick={saveReceiptSettings} style={{
                    marginTop: 16, padding: '8px 18px', borderRadius: 8, border: 'none',
                    background: C.green, color: C.white, cursor: 'pointer',
                    fontFamily: FONT, fontSize: 13, fontWeight: 700,
                }}>
                    {t('save') || 'Save'}
                </button>
            </div>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Payroll
// ═════════════════════════════════════════════════════════════════════════════

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const METHOD_OPTIONS = ['cash', 'bank_transfer', 'cash_from_till'];
const TIP_RULES = ['equal_daily_on_shift', 'proportional_hours', 'none'];

const PayrollSection = ({ onToast }) => {
    const { t } = useLanguage();
    const { hasFeature } = useAuth();
    const [cfg, setCfg] = useState(null);
    const [loadingCfg, setLoadingCfg] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        payrollConfigService.get()
            .then(data => setCfg(data))
            .catch(() => onToast('error', t('failed_to_load') || 'Failed to load'))
            .finally(() => setLoadingCfg(false));
    }, []);

    const save = async () => {
        try {
            setSaving(true);
            const updated = await payrollConfigService.update(cfg);
            setCfg(updated);
            onToast('success', t('saved') || 'Saved');
        } catch {
            onToast('error', t('failed_to_save') || 'Failed to save');
        } finally { setSaving(false); }
    };

    const set = (key, value) => setCfg(prev => ({ ...prev, [key]: value }));

    const monthlyPayDay = ((cfg?.monthly_cutoff_day || 31) + (cfg?.monthly_pay_offset_days || 0)) > 31
        ? ((cfg?.monthly_cutoff_day || 31) + (cfg?.monthly_pay_offset_days || 0)) - 31
        : (cfg?.monthly_cutoff_day || 31) + (cfg?.monthly_pay_offset_days || 0);

    const setMonthlyPayDay = (day) => {
        const cutoff = cfg?.monthly_cutoff_day || 31;
        const offset = day >= cutoff ? day - cutoff : (31 - cutoff) + day;
        set('monthly_pay_offset_days', offset);
    };

    if (loadingCfg) return <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('loading')}</div>;
    if (!cfg) return <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>{t('failed_to_load')}</div>;

    return (
        <div>
            <SectionHeader
                title={t('payroll_configuration') || 'Payroll Configuration'}
                description={t('payroll_config_description') || 'Configure cutoff dates, pay-date offsets, default payment methods, and other payroll rules. Changes apply to newly created pay runs.'}
            />

            <LockOverlay locked={!hasFeature('hr')} requiredPlan="Pro">
                <div>
                    <SubLabel>{t('monthly_payroll') || 'Monthly Payroll'}</SubLabel>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 500, marginBottom: 20 }}>
                        <div>
                            <label style={labelStyle}>{t('cutoff_day') || 'Cutoff Day'}</label>
                            <input type="number" min={1} max={31} value={cfg.monthly_cutoff_day} onChange={e => set('monthly_cutoff_day', parseInt(e.target.value) || 31)} style={inp()} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pay_day') || 'Pay Day'}</label>
                            <input type="number" min={1} max={31} value={monthlyPayDay} onChange={e => setMonthlyPayDay(parseInt(e.target.value) || 1)} style={inp()} />
                        </div>
                    </div>

                    <SubLabel>{t('weekly_payroll') || 'Weekly Payroll'}</SubLabel>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 500, marginBottom: 20 }}>
                        <div>
                            <label style={labelStyle}>{t('cutoff_day_of_week') || 'Cutoff Day of Week'}</label>
                            <select value={cfg.weekly_cutoff_day_of_week} onChange={e => set('weekly_cutoff_day_of_week', parseInt(e.target.value))} style={inp({ height: 38 })}>
                                {DAY_NAMES.slice(1).map((d, i) => <option key={i + 1} value={i + 1}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('pay_offset_days') || 'Pay Offset (days)'}</label>
                            <input type="number" min={0} max={7} value={cfg.weekly_pay_offset_days} onChange={e => set('weekly_pay_offset_days', parseInt(e.target.value) || 0)} style={inp()} />
                        </div>
                    </div>

                    <SubLabel>{t('default_payment_methods') || 'Default Payment Methods'}</SubLabel>
                    <div className="portal-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, maxWidth: 700, marginBottom: 20 }}>
                        <div>
                            <label style={labelStyle}>{t('monthly') || 'Monthly'}</label>
                            <select value={cfg.default_method_monthly} onChange={e => set('default_method_monthly', e.target.value)} style={inp({ height: 38 })}>
                                {METHOD_OPTIONS.map(m => <option key={m} value={m}>{t(m) || m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('weekly') || 'Weekly'}</label>
                            <select value={cfg.default_method_weekly} onChange={e => set('default_method_weekly', e.target.value)} style={inp({ height: 38 })}>
                                {METHOD_OPTIONS.map(m => <option key={m} value={m}>{t(m) || m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>{t('daily') || 'Daily'}</label>
                            <select value={cfg.default_method_daily} onChange={e => set('default_method_daily', e.target.value)} style={inp({ height: 38 })}>
                                {METHOD_OPTIONS.map(m => <option key={m} value={m}>{t(m) || m}</option>)}
                            </select>
                        </div>
                    </div>

                    <SubLabel>{t('rules') || 'Rules'}</SubLabel>
                    <div className="portal-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 500, marginBottom: 28 }}>
                        <div>
                            <label style={labelStyle}>{t('overtime_threshold') || 'Overtime Threshold (hrs/week)'}</label>
                            <input type="number" min={0} max={80} value={cfg.overtime_threshold_hours_week} onChange={e => set('overtime_threshold_hours_week', parseInt(e.target.value) || 40)} style={inp()} />
                        </div>
                        <div>
                            <label style={labelStyle}>{t('tip_distribution_rule') || 'Tip Distribution Rule'}</label>
                            <select value={cfg.tip_distribution_rule} onChange={e => set('tip_distribution_rule', e.target.value)} style={inp({ height: 38 })}>
                                {TIP_RULES.map(r => <option key={r} value={r}>{t(r) || r.replace(/_/g, ' ')}</option>)}
                            </select>
                        </div>
                    </div>

                    <SaveBtn onClick={save} saving={saving} t={t} />
                </div>
            </LockOverlay>
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// SECTION: Printing
// ═════════════════════════════════════════════════════════════════════════════

const IconPrinterGroup = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
    </svg>
);

// Backend stores naive UTC datetimes (no 'Z' suffix in the JSON). Treat
// offset-less strings as UTC, then all display uses the local timezone.
const parseUtc = (s) => {
    if (!s) return null;
    const iso = /Z$|[+-]\d{2}:\d{2}$/.test(s) ? s : `${s}Z`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (s) => {
    const d = parseUtc(s);
    return d ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
};

const ONLINE_WINDOW_MS = 2 * 60 * 1000; // polled within the last 2 minutes = online

const copyText = async (text) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch { /* fall through to legacy path */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch { return false; }
};

const Pill = ({ color, bg, border, children }) => (
    <span style={{
        fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
        textTransform: 'uppercase', color, background: bg,
        border: `1px solid ${border || 'transparent'}`,
        padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap', flexShrink: 0,
    }}>
        {children}
    </span>
);

const ROLE_PILL = {
    receipt: { color: C.green, bg: C.greenLight, border: C.greenBorder },
    kitchen: { color: C.amber, bg: C.amberLight, border: C.amberBorder },
    bar:     { color: C.blue,  bg: C.blueLight,  border: C.blueBorder },
};

const JOB_PILL = {
    queued:  { color: C.amber,   bg: C.amberLight,   border: C.amberBorder },
    fetched: { color: C.blue,    bg: C.blueLight,    border: C.blueBorder },
    printed: { color: C.success, bg: C.successLight, border: 'transparent' },
    failed:  { color: C.red,     bg: C.redLight,     border: C.redBorder },
};

// Shared modal shell for the printing section: visible close button +
// Escape-to-dismiss, per portal modal rules.
const PrintModalShell = ({ title, onClose, children, maxWidth = 460 }) => {
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
        >
            <div
                className="portal-modal-card"
                onClick={e => e.stopPropagation()}
                style={{
                    background: C.white, borderRadius: 14, padding: 24, width: '100%', maxWidth,
                    boxShadow: '0 20px 50px rgba(0,0,0,0.25)', maxHeight: '90vh', overflowY: 'auto',
                    fontFamily: FONT, boxSizing: 'border-box',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
                    <div style={{ fontFamily: FONT, fontSize: 16, fontWeight: 800, color: C.text }}>{title}</div>
                    <button onClick={onClose} aria-label="Close" style={{
                        background: 'none', border: 'none', cursor: 'pointer', fontSize: 22,
                        lineHeight: 1, color: C.mutedLight, padding: '4px 8px', flexShrink: 0,
                    }}>×</button>
                </div>
                {children}
            </div>
        </div>
    );
};

// Maps an eposClient probePrinter() result to a plain-language outcome plus
// a severity level used only for the message box color. The probe is advice,
// never a gate — every branch here is purely cosmetic, it never affects
// whether the printer got saved.
const epsonProbeOutcome = (result, t) => {
    if (!result) return null;
    if (result.reachable && result.eposAvailable) {
        if (result.model) {
            return {
                level: 'success',
                text: `${t('epson_probe_detected_prefix') || 'Detected:'} ${result.model} · ${t('epson_probe_epos_available') || 'ePOS-Print available'}`,
            };
        }
        return {
            level: 'success',
            text: t('epson_probe_epos_available_unknown_model') || 'ePOS-Print available, but the model could not be identified',
        };
    }
    if (result.reachable && !result.eposAvailable) {
        return { level: 'warn', text: t('epson_probe_no_epos') || "Reachable, but this model doesn't support ePOS-Print" };
    }
    return { level: 'error', text: t('epson_probe_unreachable') || 'Nothing answered at that address' };
};

const PROBE_BOX_STYLE = {
    success: { color: C.success, bg: C.successLight, border: 'transparent' },
    warn:    { color: C.amber,   bg: C.amberLight,   border: C.amberBorder },
    error:   { color: C.red,     bg: C.redLight,      border: C.redBorder },
};

// Add Printer modal — two phases: the form, then a one-time reveal. For a
// CloudPRNT (Star) printer the reveal is the one-time polling URL (token is
// only ever returned by the create call). For an Epson (network) printer
// there is no URL to copy — the reveal shows the probe outcome instead.
const AddPrinterModal = ({ onClose, onCreated, onProbed, onToast }) => {
    const { t } = useLanguage();
    const [name, setName] = useState('');
    const [role, setRole] = useState('receipt');
    const [protocol, setProtocol] = useState('cloudprnt');
    const [ip, setIp] = useState('');
    const [saving, setSaving] = useState(false);
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState(false);
    const [probing, setProbing] = useState(false);
    const [probeOutcome, setProbeOutcome] = useState(null);

    // The probe promise chain below is fire-and-forget by design (it must
    // never block save), which means it can still be resolving after the
    // tenant clicks Done/closes the modal — a realistic case, since an
    // off-right-now printer rides the probe's full ~4s timeout. Guard the
    // modal's own state setters so a late resolution never calls setState
    // on an unmounted component. Same pattern as MarketingPage.js.
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    const isEpson = protocol === 'epson';
    const canSave = !!name.trim() && (!isEpson || !!ip.trim()) && !saving;

    const handleCreate = async () => {
        const trimmed = name.trim();
        const trimmedIp = ip.trim();
        if (!trimmed || saving || (isEpson && !trimmedIp)) return;
        setSaving(true);
        try {
            const opts = isEpson ? { protocol: 'epson', ip_address: trimmedIp } : {};
            const printer = await printingService.createPrinter(trimmed, role, opts);
            setCreated(printer);
            onCreated();
            // Probe runs after the printer is already saved and never gates
            // the save itself — a printer that's simply off right now isn't
            // a configuration error. Fired without awaiting so a slow or
            // hanging probe can never delay `saving` going back to false.
            if (isEpson && host.type !== 'capacitor') {
                // The probe talks plain HTTP to a LAN address. That only
                // works under Capacitor, where CapacitorHttp routes fetch
                // through the native HTTP stack. Served over HTTPS in a
                // desktop browser the request is blocked as mixed content
                // before it leaves the page, so the probe would ALWAYS come
                // back "Nothing answered at that address" — a false negative
                // about a printer that may be perfectly fine. Say so honestly
                // instead of running a check we know cannot succeed.
                const outcome = {
                    level: 'warn',
                    text: t('epson_probe_needs_native_app')
                        || "Can't check this printer from a browser — open the Manager app on a device connected to the same Wi-Fi as the printer.",
                };
                onProbed && onProbed(printer.id, outcome);
                if (isMountedRef.current) setProbeOutcome(outcome);
            } else if (isEpson) {
                setProbing(true);
                probePrinter(trimmedIp)
                    .then(result => epsonProbeOutcome(result, t))
                    .catch(() => epsonProbeOutcome({ reachable: false, eposAvailable: false, model: null }, t))
                    .then(outcome => {
                        // onProbed still fires unconditionally: it updates
                        // NetworkPrinterManager (the parent), which normally
                        // outlives this modal — closing the modal must not
                        // drop the eventual probe result from the printer
                        // list. Only this modal's own state is guarded.
                        onProbed && onProbed(printer.id, outcome);
                        if (!isMountedRef.current) return;
                        setProbeOutcome(outcome);
                        setProbing(false);
                    });
            }
        } catch {
            onToast('error', t('failed_to_create_printer') || 'Failed to add printer');
        } finally { setSaving(false); }
    };

    const url = created && created.protocol !== 'epson' ? printingService.getCloudprntUrl(created.token) : '';

    const handleCopy = async () => {
        const ok = await copyText(url);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    return (
        <PrintModalShell title={t('add_printer') || 'Add Printer'} onClose={onClose} maxWidth={created ? 520 : 420}>
            {!created ? (
                <div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>{t('printer_name') || 'Printer Name'}</label>
                        <input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            placeholder={t('printer_name_placeholder') || 'e.g. Front Counter'}
                            autoFocus
                            style={inp({ height: 40 })}
                        />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                        <label style={labelStyle}>{t('printer_protocol') || 'Connection'}</label>
                        <select value={protocol} onChange={e => setProtocol(e.target.value)} style={inp({ height: 40 })}>
                            <option value="cloudprnt">{t('protocol_star_cloudprnt') || 'Star (CloudPRNT)'}</option>
                            <option value="epson">{t('protocol_epson_network') || 'Epson (network)'}</option>
                        </select>
                    </div>
                    {isEpson && (
                        <div style={{ marginBottom: 14 }}>
                            <label style={labelStyle}>{t('printer_ip_address') || 'IP Address'}</label>
                            <input
                                value={ip}
                                onChange={e => setIp(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                                placeholder={t('printer_ip_placeholder') || 'e.g. 192.168.1.50'}
                                style={inp({ height: 40 })}
                            />
                        </div>
                    )}
                    <div style={{ marginBottom: 22 }}>
                        <label style={labelStyle}>{t('role') || 'Role'}</label>
                        <select value={role} onChange={e => setRole(e.target.value)} style={inp({ height: 40 })}>
                            <option value="receipt">{t('receipt') || 'Receipt'}</option>
                            <option value="kitchen">{t('kitchen') || 'Kitchen'}</option>
                            <option value="bar">{t('bar') || 'Bar'}</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button onClick={onClose} style={{
                            padding: '10px 18px', borderRadius: 9, border: `1.5px solid ${C.border}`,
                            background: C.bg, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.muted,
                        }}>
                            {t('cancel') || 'Cancel'}
                        </button>
                        <button onClick={handleCreate} disabled={!canSave} style={{
                            padding: '10px 20px', borderRadius: 9, border: 'none',
                            background: C.green, color: C.white,
                            cursor: canSave ? 'pointer' : 'not-allowed',
                            opacity: canSave ? 1 : 0.6,
                            fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        }}>
                            {saving ? (t('adding') || 'Adding...') : (t('add_printer') || 'Add Printer')}
                        </button>
                    </div>
                </div>
            ) : (
                <div>
                    <div style={{ fontFamily: FONT, fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 12 }}>
                        {t('printer_added') || 'Printer added'} — {created.name}
                    </div>
                    {created.protocol === 'epson' ? (
                        <div>
                            <label style={labelStyle}>{t('printer_ip_address') || 'IP Address'}</label>
                            <div style={{
                                fontFamily: 'monospace', fontSize: 12, color: C.text,
                                background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 8,
                                padding: '10px 12px', marginBottom: 12, wordBreak: 'break-all', lineHeight: 1.5,
                            }}>
                                {created.ip_address}
                            </div>
                            <label style={labelStyle}>{t('printer_probe_result') || 'Connection Check'}</label>
                            <div style={{
                                fontFamily: FONT, fontSize: 12, lineHeight: 1.5, borderRadius: 8,
                                padding: '10px 12px', marginBottom: 20,
                                color: probing ? C.muted : probeOutcome ? PROBE_BOX_STYLE[probeOutcome.level].color : C.muted,
                                background: probing ? C.bg : probeOutcome ? PROBE_BOX_STYLE[probeOutcome.level].bg : C.bg,
                                border: `1px solid ${probing ? C.border : probeOutcome ? PROBE_BOX_STYLE[probeOutcome.level].border : C.border}`,
                            }}>
                                {probing ? (t('epson_probe_checking') || 'Checking the printer…') : (probeOutcome ? probeOutcome.text : '')}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label style={labelStyle}>{t('cloudprnt_url') || 'CloudPRNT URL'}</label>
                            <div style={{
                                display: 'flex', gap: 8, alignItems: 'stretch', marginBottom: 10,
                                flexWrap: 'wrap',
                            }}>
                                <code style={{
                                    flex: '1 1 220px', minWidth: 0, fontFamily: 'monospace', fontSize: 12, color: C.text,
                                    background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 8,
                                    padding: '10px 12px', wordBreak: 'break-all', lineHeight: 1.5,
                                }}>
                                    {url}
                                </code>
                                <button onClick={handleCopy} style={{
                                    padding: '0 16px', minHeight: 42, borderRadius: 8, border: 'none', flexShrink: 0,
                                    background: copied ? C.success : C.green, color: C.white, cursor: 'pointer',
                                    fontFamily: FONT, fontSize: 12.5, fontWeight: 700, alignSelf: 'center',
                                }}>
                                    {copied ? (t('copied') || 'Copied!') : (t('copy') || 'Copy')}
                                </button>
                            </div>
                            <div style={{
                                fontFamily: FONT, fontSize: 12, color: C.amber, background: C.amberLight,
                                border: `1px solid ${C.amberBorder}`, borderRadius: 8, padding: '10px 12px',
                                lineHeight: 1.5, marginBottom: 20,
                            }}>
                                {t('cloudprnt_url_note') || 'This URL is shown only once — copy and save it now, then enter it in the printer’s CloudPRNT settings.'}
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button onClick={onClose} style={{
                            padding: '10px 22px', borderRadius: 9, border: 'none',
                            background: C.green, color: C.white, cursor: 'pointer',
                            fontFamily: FONT, fontSize: 13, fontWeight: 700,
                        }}>
                            {t('done') || 'Done'}
                        </button>
                    </div>
                </div>
            )}
        </PrintModalShell>
    );
};

const DeletePrinterModal = ({ printer, onClose, onConfirm, deleting }) => {
    const { t } = useLanguage();
    return (
        <PrintModalShell title={t('delete_printer') || 'Delete Printer'} onClose={onClose} maxWidth={420}>
            <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 22 }}>
                {t('delete_printer_confirm') || 'Delete this printer? Its CloudPRNT URL stops working immediately.'}
                <div style={{ marginTop: 8, fontWeight: 700, color: C.text }}>{printer.name}</div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={onClose} style={{
                    padding: '10px 18px', borderRadius: 9, border: `1.5px solid ${C.border}`,
                    background: C.bg, cursor: 'pointer', fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.muted,
                }}>
                    {t('cancel') || 'Cancel'}
                </button>
                <button onClick={onConfirm} disabled={deleting} style={{
                    padding: '10px 20px', borderRadius: 9, border: 'none',
                    background: C.red, color: C.white, cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.7 : 1, fontFamily: FONT, fontSize: 13, fontWeight: 700,
                }}>
                    {deleting ? (t('deleting') || 'Deleting...') : (t('delete') || 'Delete')}
                </button>
            </div>
        </PrintModalShell>
    );
};

const NetworkPrinterManager = ({ onToast }) => {
    const { t } = useLanguage();
    const [printers, setPrinters] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [testingId, setTestingId] = useState(null);
    // Epson printers never poll the backend, so there's no server-side field
    // to read their probe outcome back from — this is session-local, keyed
    // by printer id, populated the moment AddPrinterModal finishes probing.
    const [probeStatuses, setProbeStatuses] = useState({});

    // Guards the onProbed callback below: AddPrinterModal's probe promise
    // is fire-and-forget and can resolve after this manager itself has
    // unmounted (e.g. the tenant switches away from the network printing
    // method while a probe is still in flight).
    const isMountedRef = useRef(true);
    useEffect(() => () => { isMountedRef.current = false; }, []);

    const load = useCallback(async (silent = false) => {
        try {
            const [ps, js] = await Promise.all([
                printingService.listPrinters(),
                printingService.recentJobs(10),
            ]);
            setPrinters(ps || []);
            setJobs(js || []);
        } catch {
            if (!silent) onToast('error', t('failed_to_load_printers') || 'Failed to load printers');
        } finally { setLoading(false); }
    }, [onToast, t]);

    useEffect(() => {
        load();
        // Light refresh so the online indicator and job statuses track the
        // printers' polling without a manual reload.
        const timer = setInterval(() => load(true), 30000);
        return () => clearInterval(timer);
    }, [load]);

    const handleTest = async (printer) => {
        if (testingId) return;
        setTestingId(printer.id);
        try {
            await printingService.testPrint(printer.id);
            onToast('success', t('test_print_queued') || 'Test print queued');
            load(true);
        } catch {
            onToast('error', t('test_print_failed') || 'Test print failed');
        } finally { setTestingId(null); }
    };

    const handleDelete = async () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        try {
            await printingService.deletePrinter(deleteTarget.id);
            onToast('success', t('printer_deleted') || 'Printer deleted');
            setDeleteTarget(null);
            load(true);
        } catch {
            onToast('error', t('failed_to_delete_printer') || 'Failed to delete printer');
        } finally { setDeleting(false); }
    };

    const seenInfo = (p) => {
        const d = parseUtc(p.last_seen_at);
        if (!d) return { dot: C.mutedLight, label: t('never_connected') || 'Never connected', detail: '' };
        if (Date.now() - d.getTime() < ONLINE_WINDOW_MS) {
            return { dot: C.success, label: t('online') || 'Online', detail: '' };
        }
        return {
            dot: C.amber,
            label: t('offline') || 'Offline',
            detail: `${t('last_seen') || 'Last seen'} ${fmtDateTime(p.last_seen_at)}`,
        };
    };

    const jobTypeLabel = (jt) => {
        if (jt === 'test') return t('test') || 'Test';
        return t(jt) || jt;
    };

    const addBtn = (
        <button onClick={() => setShowAdd(true)} style={{
            padding: '9px 18px', borderRadius: 9, border: 'none',
            background: C.green, color: C.white, cursor: 'pointer',
            fontFamily: FONT, fontSize: 13, fontWeight: 700,
        }}>
            + {t('add_printer') || 'Add Printer'}
        </button>
    );

    return (
        <div>
            <GroupCard
                icon={<IconPrinterGroup />}
                title={t('network_printers') || 'Network Printers'}
                description={t('network_printers_description') || 'Wi-Fi printers on your network — Star printers poll Aqana via CloudPRNT, Epson printers are sent jobs directly.'}
            >
                {loading ? (
                    <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted, padding: '12px 0' }}>
                        {t('loading') || 'Loading...'}
                    </div>
                ) : printers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '28px 12px' }}>
                        <div style={{ color: C.mutedLight, marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
                            <IconPrinterGroup />
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                            {t('no_printers_yet') || 'No printers yet'}
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.muted, marginBottom: 16, lineHeight: 1.5 }}>
                            {t('no_printers_hint') || 'Add a Star or Epson printer to start sending it print jobs over Wi-Fi.'}
                        </div>
                        {addBtn}
                    </div>
                ) : (
                    <div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                            {printers.map(p => {
                                const info = seenInfo(p);
                                return (
                                    <div key={p.id} className="portal-wrap" style={{
                                        display: 'flex', alignItems: 'center', gap: 12,
                                        border: `1.5px solid ${C.border}`, borderRadius: 10,
                                        padding: '12px 14px', background: C.white,
                                    }}>
                                        <span title={info.detail || info.label} style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: info.dot, flexShrink: 0,
                                        }} />
                                        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: C.text, wordBreak: 'break-word' }}>
                                                    {p.name}
                                                </span>
                                                <Pill {...(ROLE_PILL[p.role] || ROLE_PILL.receipt)}>{t(p.role) || p.role}</Pill>
                                            </div>
                                            <div style={{ fontFamily: FONT, fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                                                {info.label}{info.detail ? ` · ${info.detail}` : ''}
                                            </div>
                                            {p.protocol === 'epson' && (
                                                <div style={{ fontFamily: FONT, fontSize: 11.5, color: C.muted, marginTop: 2 }}>
                                                    {p.ip_address}
                                                    {probeStatuses[p.id] ? ` · ${probeStatuses[p.id].text}` : ''}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                                            {/* Test Print only enqueues a job. A CloudPRNT (Star) printer
                                                polls the queue and picks it up; an Epson printer never
                                                polls anything, so the job just sits there while the UI
                                                claims success. Hide the button rather than keep lying
                                                about it — direct Epson test printing needs the POS device
                                                to do the POST, which the portal can't drive. */}
                                            {p.protocol === 'epson' ? (
                                                <span style={{
                                                    fontFamily: FONT, fontSize: 11.5, color: C.mutedLight,
                                                    maxWidth: 190, textAlign: 'right', lineHeight: 1.35,
                                                }}>
                                                    {t('test_print_unavailable_network_printer')
                                                        || "Test print isn't available for network printers yet"}
                                                </span>
                                            ) : (
                                            <button
                                                onClick={() => handleTest(p)}
                                                disabled={testingId === p.id}
                                                style={{
                                                    padding: '8px 14px', borderRadius: 8,
                                                    border: `1.5px solid ${C.greenBorder}`, background: C.greenLight,
                                                    color: C.green, cursor: testingId === p.id ? 'wait' : 'pointer',
                                                    fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                                    opacity: testingId === p.id ? 0.7 : 1,
                                                }}
                                            >
                                                {testingId === p.id ? (t('loading') || 'Loading...') : (t('test_print') || 'Test Print')}
                                            </button>
                                            )}
                                            <button
                                                onClick={() => setDeleteTarget(p)}
                                                aria-label={t('delete_printer') || 'Delete Printer'}
                                                style={{
                                                    padding: '8px 10px', borderRadius: 8,
                                                    border: `1.5px solid ${C.border}`, background: C.white,
                                                    color: C.red, cursor: 'pointer',
                                                    fontFamily: FONT, fontSize: 12, fontWeight: 700,
                                                }}
                                            >
                                                {t('delete') || 'Delete'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {addBtn}
                    </div>
                )}
            </GroupCard>

            <GroupCard
                icon={<IconPrinterGroup />}
                title={t('recent_print_jobs') || 'Recent Print Jobs'}
                description={t('recent_print_jobs_description') || 'The last 10 jobs sent to your network printers.'}
                accent={C.blue}
                accentBg={C.blueLight}
            >
                {jobs.length === 0 ? (
                    <div style={{ fontFamily: FONT, fontSize: 12.5, color: C.mutedLight, fontStyle: 'italic', padding: '4px 0' }}>
                        {t('no_print_jobs_yet') || 'No print jobs yet'}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {jobs.map((j, i) => (
                            <div key={j.id} className="portal-wrap" style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 2px',
                                borderTop: i === 0 ? 'none' : `1px solid ${C.border}`,
                            }}>
                                <span style={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: C.text, flex: '1 1 90px', minWidth: 0 }}>
                                    {jobTypeLabel(j.job_type)}
                                </span>
                                <Pill {...(JOB_PILL[j.status] || JOB_PILL.queued)}>
                                    {t(`job_status_${j.status}`) || j.status}
                                </Pill>
                                <span style={{ fontFamily: FONT, fontSize: 11.5, color: C.muted, flexShrink: 0 }}>
                                    {fmtDateTime(j.created_at)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </GroupCard>

            {showAdd && (
                <AddPrinterModal
                    onClose={() => setShowAdd(false)}
                    onCreated={() => load(true)}
                    onProbed={(id, outcome) => {
                        if (!isMountedRef.current) return;
                        setProbeStatuses(prev => ({ ...prev, [id]: outcome }));
                    }}
                    onToast={onToast}
                />
            )}
            {deleteTarget && (
                <DeletePrinterModal
                    printer={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onConfirm={handleDelete}
                    deleting={deleting}
                />
            )}
        </div>
    );
};

// ─── Cash drawer policy (method === 'network') ────────────────────────────────
// Which payment methods pop the drawer, stored in tenant config as
// config.printing.drawer = {cash, card, mobile, split, gift_card} (all
// booleans). An absent block means cash-only — the server applies that same
// default in backend/app/services/drawer_policy.py, so this UI must never
// invent a different one. Kept separate from NetworkPrinterManager: this is
// a tenant-wide policy over payment methods, not a per-printer device
// setting like the printers/jobs lists above it.
const DRAWER_METHODS = [
    ['cash', 'drawer_open_on_cash'],
    ['card', 'drawer_open_on_card'],
    ['mobile', 'drawer_open_on_mobile'],
    ['split', 'drawer_open_on_split'],
    ['gift_card', 'drawer_open_on_gift_card'],
];

// The server's fallback, mirrored. MUST stay in step with DEFAULT_POLICY in
// backend/app/services/drawer_policy.py, which applies it PER KEY: a stored
// block that omits `cash` still opens the drawer on cash. Seeding from a
// bare `{cash: true}` and persisting one key at a time is what let this
// panel render cash unchecked on a till that opens on cash.
const DEFAULT_DRAWER = {
    cash: true, card: false, mobile: false, split: false, gift_card: false,
};

const DrawerPolicyPanel = ({ config, onToast }) => {
    const { t } = useLanguage();
    const [drawer, setDrawer] = useState(() => ({
        ...DEFAULT_DRAWER, ...(config?.printing?.drawer || {}),
    }));
    const [savingKey, setSavingKey] = useState(null);

    const setDrawerMethod = async (key, value) => {
        const prev = drawer;
        const next = { ...drawer, [key]: value };
        setDrawer(next);
        setSavingKey(key);
        try {
            // Write the WHOLE policy, not just the key that changed: a
            // partial block reads back with missing keys, and the panel
            // would then show a default it never actually persisted.
            // Backend smart_merge still leaves printing.method and the usb
            // mapping alone.
            await tenantService.updateTenantConfig({ printing: { drawer: next } });
            onToast('success', t('updated'));
        } catch {
            setDrawer(prev);
            onToast('error', t('failed_to_save') || 'Failed to save');
        } finally { setSavingKey(null); }
    };

    return (
        <GroupCard
            icon={<IconPrinterGroup />}
            title={t('cash_drawer') || 'Cash Drawer'}
            description={t('cash_drawer_description') || 'Choose which payment methods pop the drawer open.'}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {DRAWER_METHODS.map(([key, labelKey]) => (
                    <label key={key} style={{
                        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                        fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text,
                        opacity: savingKey === key ? 0.6 : 1,
                    }}>
                        <input
                            type="checkbox"
                            checked={!!drawer[key]}
                            disabled={savingKey === key}
                            onChange={(e) => setDrawerMethod(key, e.target.checked)}
                        />
                        {t(labelKey)}
                    </label>
                ))}
            </div>
            <p style={{ fontFamily: FONT, fontSize: 12, color: C.muted, marginTop: 14, marginBottom: 0, lineHeight: 1.5 }}>
                {t('drawer_split_note')}
            </p>
        </GroupCard>
    );
};

// ─── USB printer panel (method === 'usb') ─────────────────────────────────────
// Per-role OS-printer mapping stored in tenant config.printing.usb =
// { receipt, kitchen, bar }, each an OS printer name or null (system default).
// Silent, driverless OS printing needs a bridge no shipping host has — both
// the browser and the Capacitor tablet apps show a note instead of a picker.
const UsbPrinterPanel = () => {
    const { t } = useLanguage();

    const noteStyle = {
        fontFamily: FONT, fontSize: 13, color: C.muted, lineHeight: 1.55,
        background: C.bg, border: `1.5px solid ${C.border}`,
        borderRadius: 10, padding: '12px 14px',
    };

    return (
        <GroupCard
            icon={<IconPrinterGroup />}
            title={t('usb_printers') || 'USB Printers'}
        >
            <div style={noteStyle}>
                {t('usb_needs_desktop') || "Choosing which printer handles each role (receipt, kitchen, bar) isn't configurable here — the browser's print dialog is used instead."}
            </div>
        </GroupCard>
    );
};

const PrintingSection = ({ config, onToast }) => {
    const { t } = useLanguage();
    const [method, setMethod] = useState(config?.printing?.method || 'usb');
    const [savingMethod, setSavingMethod] = useState(false);

    const METHODS = [
        {
            key: 'usb',
            title: t('method_usb') || 'USB-cabled printer',
            desc: t('method_usb_desc') || 'Prints from this browser using the system print dialog.',
        },
        {
            key: 'network',
            title: t('method_network') || 'Wi-Fi / Network printer',
            desc: t('method_network_desc') || 'Printers on your network fetch jobs from Aqana directly — works from any device.',
            recommended: true,
        },
        {
            key: 'tablet_builtin',
            title: t('method_tablet_builtin') || 'Built-in tablet printer',
            desc: t('method_tablet_builtin_desc') || 'For POS tablets with an integrated receipt printer.',
            disabled: true,
        },
        {
            key: 'none',
            title: t('method_none') || 'No printing',
            desc: t('method_none_desc') || 'Run without printed receipts or kitchen tickets.',
        },
    ];

    const selectMethod = async (m) => {
        if (m === method || savingMethod) return;
        const prev = method;
        setMethod(m);
        setSavingMethod(true);
        try {
            await tenantService.updateTenantConfig({ printing: { method: m } });
            onToast('success', t('printing_method_saved') || 'Printing method saved');
        } catch {
            setMethod(prev);
            onToast('error', t('failed_to_save') || 'Failed to save');
        } finally { setSavingMethod(false); }
    };

    return (
        <div>
            <SectionHeader
                title={t('printing') || 'Printing'}
                description={t('printing_section_description') || 'Choose how receipts and kitchen tickets are printed. Network printers connect over Wi-Fi and work from any device.'}
            />

            <SubLabel mt={0}>{t('printing_method') || 'Printing Method'}</SubLabel>
            <div className="portal-grid-2" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 860, marginBottom: 28,
            }}>
                {METHODS.map(m => {
                    const selected = method === m.key;
                    return (
                        <button
                            key={m.key}
                            onClick={() => !m.disabled && selectMethod(m.key)}
                            disabled={m.disabled}
                            style={{
                                textAlign: 'left', borderRadius: 12, padding: '16px 16px 14px',
                                border: `1.5px solid ${selected ? C.green : C.border}`,
                                background: selected ? C.greenLight : C.white,
                                cursor: m.disabled ? 'not-allowed' : 'pointer',
                                opacity: m.disabled ? 0.55 : 1,
                                fontFamily: FONT, display: 'block', width: '100%',
                                boxSizing: 'border-box', transition: 'border-color 0.15s, background 0.15s',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                                <span style={{
                                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                                    border: `2px solid ${selected ? C.green : C.mutedLight}`,
                                    background: C.white, display: 'inline-flex',
                                    alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {selected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.green }} />}
                                </span>
                                <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>{m.title}</span>
                                {m.recommended && (
                                    <Pill color={C.green} bg={C.greenLight} border={C.greenBorder}>
                                        {t('recommended') || 'Recommended'}
                                    </Pill>
                                )}
                                {m.disabled && (
                                    <Pill color={C.amber} bg={C.amberLight} border={C.amberBorder}>
                                        {t('coming_soon') || 'Coming soon'}
                                    </Pill>
                                )}
                            </div>
                            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, paddingLeft: 26 }}>
                                {m.desc}
                            </div>
                        </button>
                    );
                })}
            </div>

            {method === 'usb' && <UsbPrinterPanel />}
            {method === 'network' && (
                <>
                    <NetworkPrinterManager onToast={onToast} />
                    <DrawerPolicyPanel config={config} onToast={onToast} />
                </>
            )}
        </div>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════════
const getNavItems = (t) => [
    { key: 'business',   label: t('business') || 'Business' },
    { key: 'sales',      label: t('sales') || 'Sales' },
    { key: 'catalogues', label: t('catalogues') || 'Catalogues' },
    { key: 'team',       label: t('team_and_expenses') || 'Team & Expenses' },
    { key: 'payroll',    label: t('payroll') || 'Payroll' },
    { key: 'appearance', label: t('appearance') || 'Appearance' },
    { key: 'printing',   label: t('printing') || 'Printing' },
    { key: 'eod',        label: t('end_of_day') || 'End of Day' },
];

const SECTIONS = {
    business:   BusinessSection,
    sales:      SalesSection,
    catalogues: CataloguesSection,
    team:       TeamSection,
    payroll:    PayrollSection,
    appearance: AppearanceSection,
    printing:   PrintingSection,
    eod:        EodSettingsSection,
};

// Roles & Permissions and Devices are NOT sections — they're links to the
// standalone /portal/roles and /portal/devices pages (Task 2: reachable via
// Settings instead of the sidebar). Same gate keys the old sidebar nav items
// used (NAV_FEATURE_MAP / NAV_PERMISSION_MAP, imported from PortalLayout): a
// permission failure drops the entry entirely, a feature failure keeps it
// visible with a padlock (clicking still navigates — the target page renders
// FeatureLocked itself via PortalLayout's route guard).
const EXTERNAL_NAV_LINKS = [
    { navId: 'roles',   path: '/portal/roles',   labelKey: 'roles_and_permissions', fallback: 'Roles & Permissions' },
    { navId: 'devices', path: '/portal/devices', labelKey: 'devices', fallback: 'Devices' },
];

const getExternalNavLinks = (t, hasPermission, hasFeature) => EXTERNAL_NAV_LINKS
    .filter(item => hasPermission(NAV_PERMISSION_MAP[item.navId]))
    .map(item => ({
        key: item.navId,
        path: item.path,
        label: t(item.labelKey) || item.fallback,
        locked: !!(NAV_FEATURE_MAP[item.navId] && hasFeature && !hasFeature(NAV_FEATURE_MAP[item.navId])),
    }));

const LockIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);

const PortalSettingsPage = () => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('business');
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toasts, setToasts] = useState([]);
    const { t, setReceiptTranslationSettings } = useLanguage();
    const { hasPermission, hasFeature } = useAuth();
    const externalNavLinks = getExternalNavLinks(t, hasPermission, hasFeature);

    // MobileTabSelect / desktop tabs share one onChange path: intercept the
    // two external links before they'd otherwise fall through to
    // setActiveSection (which has no 'roles'/'devices' entry in SECTIONS and
    // would render nothing).
    const handleTabSelect = useCallback((key) => {
        const link = externalNavLinks.find(l => l.key === key);
        if (link) { navigate(link.path); return; }
        setActiveSection(key);
    }, [externalNavLinks, navigate]);

    const addToast = useCallback((type, message) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []);

    useEffect(() => {
        tenantService.getTenantConfig()
            .then(data => setConfig(data))
            .catch(() => addToast('error', 'Failed to load settings'))
            .finally(() => setLoading(false));
    }, []);

    const onReload = useCallback(async () => {
        try {
            const data = await tenantService.getTenantConfig();
            setConfig(data);
        } catch { /* silent */ }
    }, []);

    const ActiveSection = SECTIONS[activeSection];

    return (
        <PortalLayout>
            <ToastContainer toasts={toasts} />

            <div style={{ fontFamily: FONT }}>
                {/* Horizontal tabs */}
                {isMobile ? (
                    <MobileTabSelect
                        tabs={[
                            ...getNavItems(t),
                            ...externalNavLinks.map(link => ({
                                key: link.key,
                                label: (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {link.label}
                                        {link.locked && <LockIcon />}
                                    </span>
                                ),
                            })),
                        ]}
                        active={activeSection}
                        onChange={handleTabSelect}
                        label={t('section') || 'Section'}
                    />
                ) : (
                <div style={{
                    display: 'flex', gap: 0, alignItems: 'center', borderBottom: `2px solid ${C.border}`,
                    marginBottom: 24, overflowX: 'auto',
                    ...(isMobile ? { whiteSpace: 'nowrap', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' } : {}),
                }}>
                    {getNavItems(t).map(item => {
                        const isActive = activeSection === item.key;
                        return (
                            <button
                                key={item.key}
                                onClick={() => setActiveSection(item.key)}
                                style={{
                                    padding: '10px 18px', border: 'none', cursor: 'pointer', flexShrink: 0,
                                    background: 'transparent', fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                    color: isActive ? C.green : C.muted,
                                    borderBottom: `2px solid ${isActive ? C.green : 'transparent'}`,
                                    marginBottom: -2, whiteSpace: 'nowrap',
                                    transition: 'color 0.15s, border-color 0.15s',
                                }}
                            >
                                {item.label}
                            </button>
                        );
                    })}
                    {externalNavLinks.length > 0 && (
                        <div style={{ width: 1, alignSelf: 'stretch', background: C.border, margin: '0 4px 2px', flexShrink: 0 }} />
                    )}
                    {externalNavLinks.map(link => (
                        <button
                            key={link.key}
                            onClick={() => navigate(link.path)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '10px 18px', border: 'none', cursor: 'pointer', flexShrink: 0,
                                background: 'transparent', fontFamily: FONT, fontSize: 13, fontWeight: 600,
                                color: C.muted, marginBottom: -2, whiteSpace: 'nowrap',
                            }}
                        >
                            {link.label}
                            {link.locked && <LockIcon />}
                        </button>
                    ))}
                </div>
                )}

                {/* Content */}
                {loading ? (
                    <div style={{ fontSize: 14, color: C.muted, paddingTop: 60, textAlign: 'center' }}>
                        {t('loading_settings') || 'Loading settings...'}
                    </div>
                ) : config ? (
                    <ActiveSection
                        config={config}
                        onToast={addToast}
                        onReload={onReload}
                        setReceiptTranslationSettings={setReceiptTranslationSettings}
                    />
                ) : (
                    <div style={{ fontSize: 13, color: C.red }}>
                        {t('failed_to_load_configuration') || 'Failed to load configuration.'}
                    </div>
                )}
            </div>
        </PortalLayout>
    );
};

export default PortalSettingsPage;
